import { isKorpoRegisterMode, isTransferRegisterMode, PRODUCTS, ProductType } from 'cart-model';
import { EventType } from 'common-event';
import { useTrans, useTransRedirect } from 'common-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as ExpertIcon } from '../../assets/images/funds.svg';
import { ReactComponent as MiddleIcon } from '../../assets/images/handshake.svg';
import { ReactComponent as ChartIcon } from '../../assets/images/chart-icon.svg';
import { logEventWithChosenProductType, logSelectPath } from '../../model/events';
import ProcessType from '../../model/ProcessType';
import ProductWizardState from '../../model/ProductWizardState';
import { productWizardActions } from '../../model/store';
import useGetAvailableFundsByUnitCategory from '../../model/useGetAvailableFundsByUnitCategory';
import Screen from '../ProductWizard/Screen';
import { ChoiceSteps } from '../ChoiceSteps/ChoiceSteps';
import { ChoiceOption } from '../ChoiceSteps/ChoiceSteps.types';
import { InfoNoteData } from '../InfoNote/InfoNote.types';
import { getDecorationIconWcagProps } from 'ui-components';

const SelectPath: React.FC = () => {
  const { trans } = useTrans('select-path');
  const [t] = useTranslation('select-path');
  const redirect = useTransRedirect();
  const dispatch = useDispatch();
  dispatch(productWizardActions.setProgress(16));

  const processType = useSelector((state: ProductWizardState) => state.uiState.processType);
  const productType = useSelector((state: ProductWizardState) => state.uiState.productType);
  const registerMode = useSelector((state: ProductWizardState) => state.cartState.registerMode);
  const isCurrentCartKorpo = isKorpoRegisterMode(registerMode);
  const isCurrentCartTransfer = isTransferRegisterMode(registerMode);
  const isIkeOrIkze = productType === ProductType.ike || productType === ProductType.ikze;
  const getAvailableFundsForUnitCategory = useGetAvailableFundsByUnitCategory();

  if (!productType) {
    redirect(Screen.SAVE_OR_INVEST.code);
    return null;
  }

  if (processType === ProcessType.expert) {
    redirect(Screen.SELECT_FUNDS.code);
    return null;
  }

  const unitCategoryCode = PRODUCTS[productType].unitCategory;
  const fundCounter = getAvailableFundsForUnitCategory(unitCategoryCode).length;

  const options: ChoiceOption[] = [
    {
      title: trans('yes', 'Yes'),
      subtitle: trans('yes-product-wizard', 'Yes, show me funds for my preferences'),
      icon: <MiddleIcon {...getDecorationIconWcagProps()}/>,
      path: Screen.CHOOSE_RISK_LEVEL.code,
      onClick: () => logSelectPath('preference list')
    },
    {
      title: trans('no', 'No'),
      subtitle: trans('no-iam-expert', 'No, I want to see all funds'),
      icon: <ExpertIcon {...getDecorationIconWcagProps()}/>,
      path: Screen.SELECT_FUNDS.code,
      currentScreenCode: Screen.SELECT_PATH.code,
      onClick: () => {
        if (isCurrentCartKorpo || isCurrentCartTransfer) {
          dispatch(productWizardActions.clearCart());
        }
        logEventWithChosenProductType(EventType.expertStart);
        logSelectPath('expert list');
      }
    }
  ];

  const notes: InfoNoteData[] = isIkeOrIkze ? [{
    title: t(`note.title.${productType}`),
    subtitle: t(`note.text.${productType}`),
    icon: <ChartIcon {...getDecorationIconWcagProps()}/>,
    transNamespace: 'select-path',
    transKey: `note.text.${productType}`,
    transValues: { fundCounter }
  }] : [];

  return (
    <ChoiceSteps
      titleKey={Screen.SELECT_PATH.code}
      subtitle={isIkeOrIkze ? t(`title.${productType}`) : undefined}
      options={options}
      notes={notes}
    />
  );
};

export default SelectPath;
